import { Alert, Button, EmptyState, FieldBlock, Input, LoadingState, Select, Tabs, TagSelect, Textarea, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { FormRow, FormScreen, FormSection } from '../../components/FormScreen.tsx'
import { api } from '../../lib/api.ts'
import { useBootstrap } from '../../lib/catalog.ts'
import { useT } from '../../lib/i18n.tsx'
import { ColorField, SwitchCard } from './catalog-shared.tsx'
import { FlowDesigner } from './FlowDesigner.tsx'
import './flows.css'

/**
 * Fluxos de trabalho (SPEC-20260815-1204) — secao do admin com duas visoes:
 * FLUXOS (cadastro + designer de processo) e ETAPAS (catalogo unico do
 * workspace, compartilhado entre fluxos; nome unico). Os quadros vinculam
 * fluxos e cada lane atua numa etapa de um fluxo.
 */

type FlowList = Awaited<ReturnType<Awaited<ReturnType<(typeof api.flows)['$get']>>['json']>>
type FlowRow = FlowList['flows'][number]
type StepList = Awaited<ReturnType<Awaited<ReturnType<(typeof api.flows.steps)['$get']>>['json']>>
type StepRow = StepList['steps'][number]

const SEM_AREA = 'SEM_AREA'

export function FlowsSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const [visao, setVisao] = useState<'fluxos' | 'etapas'>('fluxos')

  useEffect(() => {
    if (criarSinal > 0) setVisao('fluxos')
  }, [criarSinal])

  return visao === 'fluxos' ? (
    <FluxosView criarSinal={criarSinal} aoTrocarVisao={setVisao} />
  ) : (
    <EtapasView aoTrocarVisao={setVisao} />
  )
}

const seletorDeVisao = (visao: 'fluxos' | 'etapas', aoTrocar: (v: 'fluxos' | 'etapas') => void) => (
  <Tabs
    items={[
      { id: 'fluxos', label: 'Fluxos' },
      { id: 'etapas', label: 'Etapas' },
    ]}
    value={visao}
    onChange={(v) => aoTrocar(v as 'fluxos' | 'etapas')}
  />
)

// ==================================================================== Fluxos

type FormFluxo = { name: string; description: string; workItemTypeIds: string[] }
const FLUXO_VAZIO: FormFluxo = { name: '', description: '', workItemTypeIds: [] }

function FluxosView({
  criarSinal,
  aoTrocarVisao,
}: {
  criarSinal: number
  aoTrocarVisao: (v: 'fluxos' | 'etapas') => void
}) {
  const toast = useToast()
  const qc = useQueryClient()
  const bootstrap = useBootstrap()
  const traduzir = useT()
  const labelDoTipo = (tp: { nameTranslationKey: string; code: string }) => traduzir(tp.nameTranslationKey) || tp.code

  const lista = useQuery({ queryKey: ['flows'], queryFn: async () => (await api.flows.$get()).json() })

  const [busca, setBusca] = useState('')
  const [editando, setEditando] = useState<{ id: string | null } | null>(null)
  const [form, setForm] = useState<FormFluxo>(FLUXO_VAZIO)
  const [desenhando, setDesenhando] = useState<string | null>(null)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState<string | null>(null)

  // deep-link do quadro: /admin/flows?desenhar=<flowId> abre o designer
  // daquele fluxo direto (atalho no painel Fluxo/Etapas do Kanban); com
  // ?voltar=<rota>, FECHAR o designer retorna para quem chamou
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [voltarPara, setVoltarPara] = useState<string | null>(null)
  // ?destacar=<flowStepId>: o designer abre com a etapa do ITEM em destaque;
  // ?codigo=<WI-xxxx> aparece no selo sobre a etapa
  const [destacarNo, setDestacarNo] = useState<string | null>(null)
  const [codigoDoItem, setCodigoDoItem] = useState<string | null>(null)
  useEffect(() => {
    const alvo = searchParams.get('desenhar')
    if (alvo) {
      setDesenhando(alvo)
      setVoltarPara(searchParams.get('voltar'))
      setDestacarNo(searchParams.get('destacar'))
      setCodigoDoItem(searchParams.get('codigo'))
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (criarSinal > 0) {
      setForm(FLUXO_VAZIO)
      setEditando({ id: null })
      setDesenhando(null)
    }
  }, [criarSinal])

  const invalidar = () => void qc.invalidateQueries({ queryKey: ['flows'] })

  const aoFalhar = (erro: unknown) =>
    toast.push({
      tone: 'danger',
      message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.',
    })

  const criar = useMutation({
    mutationFn: async (f: FormFluxo) =>
      (await api.flows.$post({ json: { name: f.name.trim(), description: f.description.trim() || null } })).json(),
    onSuccess: (dados) => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Fluxo criado — agora desenhe o processo.' })
      // criar ja cai no designer: e o proximo passo natural
      if (dados.flow) setDesenhando(dados.flow.id)
    },
    onError: aoFalhar,
  })

  const atualizar = useMutation({
    mutationFn: async ({ id, f }: { id: string; f: FormFluxo }) =>
      (
        await api.flows[':id'].$patch({
          param: { id },
          json: { name: f.name.trim(), description: f.description.trim() || null, workItemTypeIds: f.workItemTypeIds },
        })
      ).json(),
    onSuccess: () => {
      invalidar()
      void qc.invalidateQueries({ queryKey: ['bootstrap'] })
      setEditando(null)
      toast.push({ tone: 'success', message: 'Fluxo atualizado.' })
    },
    onError: aoFalhar,
  })

  const alternarAtivo = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await api.flows[':id'].$patch({ param: { id }, json: { isActive } })).json(),
    onSuccess: invalidar,
    onError: aoFalhar,
  })

  const excluir = useMutation({
    mutationFn: async (id: string) => (await api.flows[':id'].$delete({ param: { id } })).json(),
    onSuccess: () => {
      invalidar()
      setConfirmandoExclusao(null)
      toast.push({ tone: 'success', message: 'Fluxo excluído.' })
    },
    onError: (erro) => {
      setConfirmandoExclusao(null)
      aoFalhar(erro)
    },
  })

  const flows = lista.data?.flows ?? []
  const emEdicao = editando?.id ? flows.find((f) => f.id === editando.id) : undefined

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (termo === '') return flows
    return flows.filter((f) => `${f.name} ${f.description ?? ''}`.toLowerCase().includes(termo))
  }, [flows, busca])

  const abrir = (alvo: FlowRow | null) => {
    setForm(
      alvo
        ? { name: alvo.name, description: alvo.description ?? '', workItemTypeIds: [...(alvo.workItemTypeIds ?? [])] }
        : FLUXO_VAZIO,
    )
    setEditando({ id: alvo?.id ?? null })
  }

  // -------------------------------------------------------- designer ---
  if (desenhando !== null) {
    return (
      <FlowDesigner
        flowId={desenhando}
        destacarFlowStepId={destacarNo}
        destacarItemCodigo={codigoDoItem}
        onClose={() => {
          setDesenhando(null)
          setDestacarNo(null)
          setCodigoDoItem(null)
          // veio de fora (ex.: quadro Kanban)? volta para a origem
          if (voltarPara) {
            const destino = voltarPara
            setVoltarPara(null)
            navigate(destino)
          }
        }}
      />
    )
  }

  // ------------------------------------------------------- formulário ---
  const alterado =
    editando?.id == null
      ? form.name.trim().length >= 2
      : emEdicao !== undefined &&
        (form.name.trim() !== emEdicao.name ||
          form.description.trim() !== (emEdicao.description ?? '') ||
          JSON.stringify([...form.workItemTypeIds].sort()) !== JSON.stringify([...(emEdicao.workItemTypeIds ?? [])].sort()))

  if (editando !== null) {
    const salvarValido = alterado && form.name.trim().length >= 2 && !criar.isPending && !atualizar.isPending
    return (
      <FormScreen
        title={editando.id ? 'Editar fluxo' : 'Novo fluxo'}
        subtitle={editando.id ? emEdicao?.name : undefined}
        onClose={() => setEditando(null)}
        dirty={alterado}
        dirtyHint={editando.id ? 'Tudo salvo' : 'Preencha o nome para salvar'}
        saveLabel={editando.id ? 'Salvar' : 'Criar e desenhar'}
        saveDisabled={!salvarValido}
        saving={criar.isPending || atualizar.isPending}
        onSubmit={() => {
          if (!salvarValido) return
          if (editando.id) atualizar.mutate({ id: editando.id, f: form })
          else criar.mutate(form)
        }}
      >
        <FormSection title="Informações do fluxo">
          <FormRow cols="1fr 2fr">
            <FieldBlock label="Nome do fluxo" required hint="Único no workspace — ex.: Fluxo de Entrega, Suporte N2.">
              {({ id }) => (
                <Input
                  id={id}
                  autoFocus
                  value={form.name}
                  placeholder="Ex.: Fluxo de Entrega"
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Descrição" hint="O que este processo cobre.">
              {({ id }) => (
                <Input
                  id={id}
                  value={form.description}
                  placeholder="Ex.: Da análise técnica à entrega em produção"
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          {editando.id ? (
            <FieldBlock
              label="Tipos de item deste fluxo"
              hint="Itens desses tipos seguem este fluxo, e só entram em quadros que o usam. Um tipo pode pertencer a mais de um fluxo — ao criar o item você escolhe por qual começar."
            >
              {({ id }) => (
                <TagSelect
                  id={id}
                  value={form.workItemTypeIds}
                  placeholder="Escolha os tipos de item…"
                  emptyLabel="Nenhum tipo — o fluxo fica opcional"
                  options={(bootstrap.data?.workItemTypes ?? [])
                    .filter((t) => t.isActive)
                    .map((t) => ({ value: t.id, label: labelDoTipo(t) }))}
                  onChange={(v) => setForm((f) => ({ ...f, workItemTypeIds: v }))}
                />
              )}
            </FieldBlock>
          ) : (
            <p className="adm-hint">
              Depois de criar e desenhar as etapas, edite o fluxo para escolher os <strong>tipos de item</strong> que
              pertencem a ele.
            </p>
          )}

          {editando.id ? (
            <div>
              <Button variant="secondary" icon="fas fa-route" onClick={() => setDesenhando(editando.id)}>
                Abrir o desenho do processo
              </Button>
            </div>
          ) : (
            <p className="adm-hint">Ao criar, o desenho do processo abre em seguida para você incluir as etapas.</p>
          )}
        </FormSection>
      </FormScreen>
    )
  }

  // ------------------------------------------------------------ lista ---

  const cabecalho = (
    <div className="adm-head">
      <div>
        <h1 className="adm-head__title">Fluxos de trabalho</h1>
        <p className="adm-head__desc">Desenhe processos ligando etapas. Os quadros vinculam fluxos e cada lane atua numa etapa.</p>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        {seletorDeVisao('fluxos', aoTrocarVisao)}
        <Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>
          Incluir
        </Button>
      </div>
    </div>
  )

  if (lista.isPending) return (<>{cabecalho}<LoadingState variant="list" label="Carregando os fluxos" /></>)
  if (lista.isError) {
    return (
      <>
        {cabecalho}
        <div className="adm-placeholder">
          <Alert tone="danger" title="Não foi possível carregar os fluxos"
            action={<Button variant="secondary" icon="fas fa-rotate-right" onClick={() => void lista.refetch()}>Tentar de novo</Button>}>
            Verifique a conexão e tente novamente.
          </Alert>
        </div>
      </>
    )
  }

  return (
    <>
      {cabecalho}

      <div className="adm-toolbar">
        <div className="adm-toolbar__search">
          <i className="fas fa-search" aria-hidden />
          <input type="search" placeholder="Filtrar por nome…" aria-label="Filtrar fluxos"
            value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <span className="adm-toolbar__count">
          {filtrados.length} de {flows.length} {flows.length === 1 ? 'registro' : 'registros'}
        </span>
      </div>

      {filtrados.length === 0 ? (
        <div className="adm-placeholder">
          <EmptyState icon="fas fa-route"
            title={flows.length === 0 ? 'Nenhum fluxo ainda' : 'Nenhum fluxo com esse filtro'}
            description={flows.length === 0
              ? 'Crie o primeiro fluxo e desenhe o processo ligando etapas.'
              : `Nada casa com “${busca}”.`}
            action={flows.length === 0
              ? (<Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>Novo fluxo</Button>)
              : (<Button variant="secondary" icon="fas fa-times" onClick={() => setBusca('')}>Limpar o filtro</Button>)} />
        </div>
      ) : (
        <div className="adm-list">
          {filtrados.map((row) => {
            const confirmando = confirmandoExclusao === row.id
            return (
              <div
                key={row.id}
                className={`adm-catrow${row.isActive ? '' : ' adm-catrow--off'}`}
                title="Dois cliques abrem o desenho do processo"
                // duplo clique na LINHA entra direto no desenho do fluxo
                // (pedido 2026-08-29); os botões da direita seguem com as ações
                onDoubleClick={(e) => {
                  if ((e.target as HTMLElement).closest('button')) return
                  setDesenhando(row.id)
                }}
              >
                <span className="adm-catrow__icon"><i className="fas fa-route" aria-hidden /></span>
                <span className="adm-catrow__main">
                  <span className="adm-catrow__name">{row.name}</span>
                  <span className="adm-catrow__sub">
                    {[row.description,
                      `${row.stepCount} ${row.stepCount === 1 ? 'etapa' : 'etapas'}`,
                      row.boardCount > 0 ? `${row.boardCount} ${row.boardCount === 1 ? 'quadro' : 'quadros'}` : 'sem quadros',
                    ].filter(Boolean).join(' · ')}
                  </span>
                </span>

                {confirmando ? (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--ob-danger)' }}>Excluir “{row.name}”?</span>
                    <Button variant="danger" loading={excluir.isPending} onClick={() => excluir.mutate(row.id)}>
                      Confirmar
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmandoExclusao(null)}>
                      Cancelar
                    </Button>
                  </span>
                ) : (
                  <>
                    <span className="adm-switchwrap">
                      <span className="adm-switchwrap__label">{row.isActive ? 'Ativo' : 'Inativo'}</span>
                      <button type="button" role="switch" aria-checked={row.isActive}
                        aria-label={`${row.name} está ativo`}
                        title={row.isActive ? 'Ativo — clique para desativar' : 'Inativo — clique para ativar'}
                        className={`adm-switch${row.isActive ? ' adm-switch--on' : ''}`}
                        onClick={() => alternarAtivo.mutate({ id: row.id, isActive: !row.isActive })}>
                        <span className="adm-switch__knob" aria-hidden />
                      </button>
                    </span>
                    <button type="button" className="adm-iconbtn" aria-label={`Desenhar o processo de ${row.name}`}
                      title="Desenhar o processo" onClick={() => setDesenhando(row.id)}>
                      <i className="fas fa-route" aria-hidden />
                    </button>
                    <button type="button" className="adm-iconbtn" aria-label={`Editar ${row.name}`}
                      title="Editar nome e descrição" onClick={() => abrir(row)}>
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                    <button type="button" className="adm-iconbtn adm-iconbtn--danger" disabled={row.boardCount > 0}
                      aria-label={row.boardCount > 0 ? `${row.name} está em uso por quadros` : `Excluir ${row.name}`}
                      title={row.boardCount > 0 ? 'Fluxo em uso por quadros: desvincule antes de excluir' : 'Excluir'}
                      onClick={() => setConfirmandoExclusao(row.id)}>
                      <i className="fas fa-trash-alt" aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

// ==================================================================== Etapas

type FormEtapa = {
  name: string
  description: string
  color: string | null
  companyAreaId: string | null
  expectedDurationHours: string
  entryCriteria: string
  exitCriteria: string
  isActive: boolean
}

const ETAPA_VAZIA: FormEtapa = {
  name: '',
  description: '',
  color: null,
  companyAreaId: null,
  expectedDurationHours: '',
  entryCriteria: '',
  exitCriteria: '',
  isActive: true,
}

function EtapasView({ aoTrocarVisao }: { aoTrocarVisao: (v: 'fluxos' | 'etapas') => void }) {
  const toast = useToast()
  const qc = useQueryClient()
  const bootstrap = useBootstrap()
  const t = useT()
  // rotulo da area: traducao quando a chave existe; senao a propria chave
  const rotuloDaArea = (key: string) => (t(key) === key ? key : t(key))

  const lista = useQuery({ queryKey: ['process-steps'], queryFn: async () => (await api.flows.steps.$get()).json() })

  const [busca, setBusca] = useState('')
  const [editando, setEditando] = useState<{ id: string | null } | null>(null)
  const [form, setForm] = useState<FormEtapa>(ETAPA_VAZIA)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState<string | null>(null)

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['process-steps'] })
    void qc.invalidateQueries({ queryKey: ['flows'] })
  }

  const aoFalhar = (erro: unknown) =>
    toast.push({
      tone: 'danger',
      message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.',
    })

  const corpoDe = (f: FormEtapa) => ({
    name: f.name.trim(),
    description: f.description.trim() || null,
    color: f.color,
    companyAreaId: f.companyAreaId,
    expectedDurationHours: f.expectedDurationHours.trim() === '' ? null : Math.max(1, Number(f.expectedDurationHours) || 1),
    entryCriteria: f.entryCriteria.trim() || null,
    exitCriteria: f.exitCriteria.trim() || null,
    isActive: f.isActive,
  })

  const criar = useMutation({
    mutationFn: async (f: FormEtapa) => (await api.flows.steps.$post({ json: corpoDe(f) })).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Etapa criada.' })
    },
    onError: aoFalhar,
  })

  const atualizar = useMutation({
    mutationFn: async ({ id, f }: { id: string; f: FormEtapa }) =>
      (await api.flows.steps[':id'].$patch({ param: { id }, json: corpoDe(f) })).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Etapa atualizada.' })
    },
    onError: aoFalhar,
  })

  const alternarAtivo = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await api.flows.steps[':id'].active.$post({ param: { id }, json: { isActive } })).json(),
    onSuccess: invalidar,
    onError: aoFalhar,
  })

  const excluir = useMutation({
    mutationFn: async (id: string) => (await api.flows.steps[':id'].$delete({ param: { id } })).json(),
    onSuccess: () => {
      invalidar()
      setConfirmandoExclusao(null)
      toast.push({ tone: 'success', message: 'Etapa excluída.' })
    },
    onError: (erro) => {
      setConfirmandoExclusao(null)
      aoFalhar(erro)
    },
  })

  const steps = lista.data?.steps ?? []
  const emEdicao = editando?.id ? steps.find((s) => s.id === editando.id) : undefined
  const areas = (bootstrap.data?.areas ?? []).filter((a) => a.isActive)
  const nomeDaArea = (id: string | null) => {
    if (!id) return null
    const chave = (bootstrap.data?.areas ?? []).find((a) => a.id === id)?.nameTranslationKey
    return chave ? rotuloDaArea(chave) : null
  }

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (termo === '') return steps
    return steps.filter((s) => `${s.name} ${s.code}`.toLowerCase().includes(termo))
  }, [steps, busca])

  const abrir = (alvo: StepRow | null) => {
    setForm(
      alvo
        ? {
            name: alvo.name,
            description: alvo.description ?? '',
            color: alvo.color,
            companyAreaId: alvo.companyAreaId,
            expectedDurationHours: alvo.expectedDurationHours === null ? '' : String(alvo.expectedDurationHours),
            entryCriteria: alvo.entryCriteria ?? '',
            exitCriteria: alvo.exitCriteria ?? '',
            isActive: alvo.isActive,
          }
        : ETAPA_VAZIA,
    )
    setEditando({ id: alvo?.id ?? null })
  }

  const alterado =
    editando?.id == null
      ? form.name.trim().length >= 2
      : emEdicao !== undefined &&
        JSON.stringify(corpoDe(form)) !==
          JSON.stringify(
            corpoDe({
              name: emEdicao.name,
              description: emEdicao.description ?? '',
              color: emEdicao.color,
              companyAreaId: emEdicao.companyAreaId,
              expectedDurationHours: emEdicao.expectedDurationHours === null ? '' : String(emEdicao.expectedDurationHours),
              entryCriteria: emEdicao.entryCriteria ?? '',
              exitCriteria: emEdicao.exitCriteria ?? '',
              isActive: emEdicao.isActive,
            }),
          )

  // ------------------------------------------------------- formulário ---

  if (editando !== null) {
    const salvarValido = alterado && form.name.trim().length >= 2 && !criar.isPending && !atualizar.isPending
    return (
      <FormScreen
        title={editando.id ? 'Editar etapa' : 'Nova etapa'}
        subtitle={editando.id ? emEdicao?.name : undefined}
        onClose={() => setEditando(null)}
        dirty={alterado}
        dirtyHint={editando.id ? 'Tudo salvo' : 'Preencha o nome para salvar'}
        saveLabel={editando.id ? 'Salvar' : 'Criar etapa'}
        saveDisabled={!salvarValido}
        saving={criar.isPending || atualizar.isPending}
        onSubmit={() => {
          if (!salvarValido) return
          if (editando.id) atualizar.mutate({ id: editando.id, f: form })
          else criar.mutate(form)
        }}
      >
        <FormSection title="Informações da etapa">
          <FormRow cols="2fr 2fr 1fr">
            <FieldBlock label="Nome da etapa" required hint="Único no workspace — o mesmo nome vale em todos os fluxos.">
              {({ id }) => (
                <Input
                  id={id}
                  autoFocus
                  value={form.name}
                  placeholder="Ex.: Análise Técnica"
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Área responsável" hint="Quem é o dono desta etapa do processo.">
              {({ id }) => (
                <Select<string>
                  id={id}
                  value={form.companyAreaId ?? SEM_AREA}
                  options={[
                    { value: SEM_AREA, label: 'Sem área definida' },
                    ...areas.map((a) => ({ value: a.id, label: rotuloDaArea(a.nameTranslationKey) })),
                  ]}
                  onChange={(valor) => setForm((f) => ({ ...f, companyAreaId: valor === SEM_AREA ? null : valor }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Duração esperada (h)" hint="SLA da etapa. Vazio = sem prazo.">
              {({ id }) => (
                <Input
                  id={id}
                  type="number"
                  min={1}
                  value={form.expectedDurationHours}
                  placeholder="Ex.: 8"
                  onChange={(e) => setForm((f) => ({ ...f, expectedDurationHours: e.target.value }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          <FormRow cols="1fr">
            <FieldBlock label="Descrição" hint="O que acontece nesta etapa.">
              {({ id }) => (
                <Textarea
                  id={id}
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          <FormRow cols="2fr 1fr">
            <ColorField
              hint="Pinta o cartão da etapa no desenho e nas lanes — o nome continua sendo o que comunica."
              value={form.color}
              onChange={(color) => setForm((f) => ({ ...f, color }))}
            />
            <SwitchCard
              title={form.isActive ? 'Etapa ativa' : 'Etapa inativa'}
              description={form.isActive ? 'Disponível para entrar em novos fluxos.' : 'Some das opções; fluxos que já a usam continuam.'}
              checked={form.isActive}
              onChange={(isActive) => setForm((f) => ({ ...f, isActive }))}
            />
          </FormRow>
        </FormSection>

        <FormSection title="Critérios (boas práticas de processo)">
          <FormRow cols="1fr 1fr">
            <FieldBlock label="Critérios de entrada" hint="O que precisa ser verdade para um item ENTRAR nesta etapa.">
              {({ id }) => (
                <Textarea
                  id={id}
                  rows={3}
                  value={form.entryCriteria}
                  placeholder="Ex.: demanda aprovada e priorizada"
                  onChange={(e) => setForm((f) => ({ ...f, entryCriteria: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Critérios de saída" hint="O que precisa estar pronto para o item SAIR (definition of done).">
              {({ id }) => (
                <Textarea
                  id={id}
                  rows={3}
                  value={form.exitCriteria}
                  placeholder="Ex.: especificação revisada e aprovada"
                  onChange={(e) => setForm((f) => ({ ...f, exitCriteria: e.target.value }))}
                />
              )}
            </FieldBlock>
          </FormRow>
          {emEdicao ? (
            <p className="adm-hint">
              <i className="fas fa-link" aria-hidden />{' '}
              {emEdicao.flowCount > 0
                ? `Em ${emEdicao.flowCount} ${emEdicao.flowCount === 1 ? 'fluxo' : 'fluxos'}${emEdicao.laneCount > 0 ? ` · ${emEdicao.laneCount} ${emEdicao.laneCount === 1 ? 'lane' : 'lanes'} de quadro` : ''}.`
                : 'Ainda fora de qualquer fluxo.'}
            </p>
          ) : null}
        </FormSection>
      </FormScreen>
    )
  }

  // ------------------------------------------------------------ lista ---

  const cabecalho = (
    <div className="adm-head">
      <div>
        <h1 className="adm-head__title">Etapas</h1>
        <p className="adm-head__desc">Catálogo único do workspace — a mesma etapa pode participar de vários fluxos.</p>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        {seletorDeVisao('etapas', aoTrocarVisao)}
        <Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>
          Incluir
        </Button>
      </div>
    </div>
  )

  if (lista.isPending) return (<>{cabecalho}<LoadingState variant="list" label="Carregando as etapas" /></>)
  if (lista.isError) {
    return (
      <>
        {cabecalho}
        <div className="adm-placeholder">
          <Alert tone="danger" title="Não foi possível carregar as etapas"
            action={<Button variant="secondary" icon="fas fa-rotate-right" onClick={() => void lista.refetch()}>Tentar de novo</Button>}>
            Verifique a conexão e tente novamente.
          </Alert>
        </div>
      </>
    )
  }

  return (
    <>
      {cabecalho}

      <div className="adm-toolbar">
        <div className="adm-toolbar__search">
          <i className="fas fa-search" aria-hidden />
          <input type="search" placeholder="Filtrar por nome ou código…" aria-label="Filtrar etapas"
            value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <span className="adm-toolbar__count">
          {filtradas.length} de {steps.length} {steps.length === 1 ? 'registro' : 'registros'}
        </span>
      </div>

      {filtradas.length === 0 ? (
        <div className="adm-placeholder">
          <EmptyState icon="fas fa-shoe-prints"
            title={steps.length === 0 ? 'Nenhuma etapa ainda' : 'Nenhuma etapa com esse filtro'}
            description={steps.length === 0
              ? 'Crie a primeira etapa — ela poderá entrar em qualquer fluxo.'
              : `Nada casa com “${busca}”.`}
            action={steps.length === 0
              ? (<Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>Nova etapa</Button>)
              : (<Button variant="secondary" icon="fas fa-times" onClick={() => setBusca('')}>Limpar o filtro</Button>)} />
        </div>
      ) : (
        <div className="adm-list">
          {filtradas.map((row) => {
            const confirmando = confirmandoExclusao === row.id
            const area = nomeDaArea(row.companyAreaId)
            return (
              <div key={row.id} className={`adm-catrow${row.isActive ? '' : ' adm-catrow--off'}`}>
                <span className="adm-catrow__icon" style={row.color ? { color: row.color } : undefined}>
                  <i className="fas fa-circle" aria-hidden />
                </span>
                <span className="adm-catrow__main">
                  <span className="adm-catrow__name">{row.name}</span>
                  <span className="adm-catrow__sub">
                    {[`Código ${row.code}`,
                      area,
                      row.expectedDurationHours !== null ? `SLA ${row.expectedDurationHours}h` : null,
                      row.flowCount > 0 ? `em ${row.flowCount} ${row.flowCount === 1 ? 'fluxo' : 'fluxos'}` : 'fora de fluxos',
                    ].filter(Boolean).join(' · ')}
                  </span>
                </span>

                {confirmando ? (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--ob-danger)' }}>Excluir “{row.name}”?</span>
                    <Button variant="danger" loading={excluir.isPending} onClick={() => excluir.mutate(row.id)}>
                      Confirmar
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmandoExclusao(null)}>
                      Cancelar
                    </Button>
                  </span>
                ) : (
                  <>
                    <span className="adm-switchwrap">
                      <span className="adm-switchwrap__label">{row.isActive ? 'Ativa' : 'Inativa'}</span>
                      <button type="button" role="switch" aria-checked={row.isActive}
                        aria-label={`${row.name} está ativa`}
                        title={row.isActive ? 'Ativa — clique para desativar' : 'Inativa — clique para ativar'}
                        className={`adm-switch${row.isActive ? ' adm-switch--on' : ''}`}
                        onClick={() => alternarAtivo.mutate({ id: row.id, isActive: !row.isActive })}>
                        <span className="adm-switch__knob" aria-hidden />
                      </button>
                    </span>
                    <button type="button" className="adm-iconbtn" aria-label={`Editar ${row.name}`}
                      title="Editar" onClick={() => abrir(row)}>
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                    <button type="button" className="adm-iconbtn adm-iconbtn--danger" disabled={row.flowCount > 0}
                      aria-label={row.flowCount > 0 ? `${row.name} está em fluxos e não pode ser excluída` : `Excluir ${row.name}`}
                      title={row.flowCount > 0 ? 'Etapa em fluxos: remova-a dos desenhos primeiro' : 'Excluir'}
                      onClick={() => setConfirmandoExclusao(row.id)}>
                      <i className="fas fa-trash-alt" aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
